import Cache, { CacheOptions } from "./cache";

export class CachedService<T> {
    private _cache: Cache<T>;

    constructor(name: string, private _serviceMethod: (value: any) => Promise<T>, options?: CacheOptions) {
        this._cache = new Cache<T>(name, options);
    }

    add(key: string, value: T) {
        this._cache.add(key, value);
    }

    async get(key: string, requestValue?: any) {
        const cachedValue = this._cache.get(key);

        if (!!cachedValue) {
            return cachedValue;
        }

        const data = await this._serviceMethod(requestValue ?? key);
        this._cache.add(key, data);

        return data;
    }
}

export class CachedServiceWithDynamicKey<T> {
    constructor(
        private _getName: (value: any) => string,
        private _serviceMethod: (value: any) => Promise<T>,
        private _options?: CacheOptions) {
    }

    async get(requestValue?: any) {
        const key = this._getName(requestValue);

        const service = new CachedService<T>(key, this._serviceMethod, this._options);
        return service.get(key, requestValue);
    }
}

export class CachedServiceCreator {
    constructor(private _options: CacheOptions) { }

    create<T>(name: string, serviceMethod: (value: any) => Promise<T>) {
        return new CachedService<T>(name, serviceMethod, this._options);
    }

    createDynamicKey<T>(getName: (value: any) => string, serviceMethod: (value: any) => Promise<T>) {
        return new CachedServiceWithDynamicKey<T>(getName, serviceMethod, this._options);
    }
}

export default CachedService;
